import { useEffect, useRef } from "react";
import { Mask, SpinLoading, Dialog } from "antd-mobile";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Routes, Route, matchPath, useLocation } from "react-router-dom";
import routes from "../routes";
import routeDescriptions from "../routeDescriptions";
import LeftPanel from "./components/LeftPanel";
import { SocketStates, useStores } from "../../models";
import { initDB } from "../../services/dexie";
import useNavigation from "../../hooks/useNavigation";
import TradingRoomAlert from "../../components/Wallet/TradingRoomAlert";
import ExchangeAlert from "../../components/Wallet/ExchangeAlert";

const SYNC_MASK_MAX_MS = 20000;

const AppRoutes = routes.filter(
  (route) => route.routerName === "app" || route.routerName === "all"
);

const AppRouterWrap = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: #fff;
  font-size: 14px;
`;

const AppRouter = () => {
  const { navigate } = useNavigation();
  const location = useLocation();
  const rootStore = useStores();
  const {
    websocketStore,
    authStore: { userID, publicUpdateClientID, haveBeenPassEmailCheck, setHaveBeenPassEmailCheck },
    blackListStore,
    isUserStateLoaded,
    isMobile,
    authStore,
    syncFinished,
    setSyncFinished,
    setPanel,
    panel,
    userStore: { currentUser },
  } = rootStore;
  const prevIsMobileRef = useRef(isMobile);

  useEffect(() => {
    async function startDB() {
      await initDB()
    }
    startDB()
  }, [])

  useEffect(() => {
    async function loadUserState() {
      await rootStore.load();
    }
    loadUserState();
  }, [rootStore, userID]);

  useEffect(() => {
    if (isUserStateLoaded) {
      websocketStore.connect();
    }
    return () => {
      if (isUserStateLoaded) {
        websocketStore.close()
      }
    }
  }, [isUserStateLoaded, websocketStore]);

  useEffect(() => {
    async function checkToken() {
      try {
        await authStore.httpPing();
        websocketStore.setState(SocketStates.DISCONNECTED);
      } catch (e) {
        websocketStore.setState(SocketStates.CLOSED);
        websocketStore.kick(e.message || "网路异常请重新登录");
      }
    }
    if (websocketStore.hasError) {
      checkToken();
    }
  }, [websocketStore, websocketStore.hasError, authStore]);

  useEffect(() => {
    if (isUserStateLoaded && websocketStore.ready) {
      blackListStore.publicQueryBlacklistList(userID);
    }
  }, [isUserStateLoaded, websocketStore.ready, userID, blackListStore]);

  useEffect(() => {
    if (isUserStateLoaded && websocketStore.ready && authStore.clientId) {
      publicUpdateClientID();
    }
  }, [
    isUserStateLoaded,
    websocketStore.ready,
    authStore.clientId,
    publicUpdateClientID,
  ]);

  useEffect(() => {
    if (syncFinished && !currentUser.email && !haveBeenPassEmailCheck) {
      setHaveBeenPassEmailCheck(true)
      Dialog.confirm({
        title: '是否要完善电子邮箱资讯?',
        content: `忘记密码可透过电子邮箱重置`,
        onConfirm: () => navigate('UpdateCurrentUserEmail'),
        confirmText: '前往修改',
        cancelText: '之后再说'
      });
    }
  }, [navigate, syncFinished, currentUser, haveBeenPassEmailCheck, setHaveBeenPassEmailCheck])

  // WebSocket 握手失败时 SYNC_FINISHED 不会到来，避免遮罩永久卡住
  useEffect(() => {
    if (!isUserStateLoaded || syncFinished) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setSyncFinished(true);
    }, SYNC_MASK_MAX_MS);
    return () => window.clearTimeout(timer);
  }, [isUserStateLoaded, syncFinished, setSyncFinished]);

  // 窄屏 ↔ 宽屏切换时同步布局，避免左侧栏 + 主路由各带一套底部导航
  useEffect(() => {
    if (!isUserStateLoaded) return;
    const prev = prevIsMobileRef.current;
    if (prev === isMobile) return;
    prevIsMobileRef.current = isMobile;

    const leftPanelRoutes = routeDescriptions.filter(
      (r) => r.routerName === "app" && r.isLeftPanel
    );
    const currentLeft = leftPanelRoutes.find((r) =>
      matchPath({ path: r.path, end: true }, location.pathname)
    );

    if (!isMobile) {
      // 进入 PC：左侧栏承载列表页，主区回到 Welcome
      if (currentLeft) {
        setPanel(currentLeft.key);
        navigate("Welcome", {}, { replace: true });
      }
    } else {
      // 进入移动端：主路由展示当前左侧 tab
      navigate(panel || "ChatList", {}, { replace: true });
    }
  }, [
    isMobile,
    isUserStateLoaded,
    location.pathname,
    navigate,
    panel,
    setPanel,
  ]);

  if (!isUserStateLoaded) return null;

  return (
    <AppRouterWrap>
      <Mask visible={!syncFinished}>
        <Center>
          <SpinLoading></SpinLoading>
          <div>{websocketStore.stateText || "同步中"}...</div>
        </Center>
      </Mask>
      {!isMobile && <LeftPanel />}
      <TradingRoomAlert />
      <ExchangeAlert />
      <Routes>
        {AppRoutes.map(({ key, path, Component }) => {
          return <Route key={key} path={path} element={<Component />} />;
        })}
      </Routes>
    </AppRouterWrap>
  );
};

export default observer(AppRouter);
